import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  FileText, Search, Filter, CheckCircle, Clock, XCircle,
  DollarSign, Calendar, User, ExternalLink, RefreshCw, Download
} from 'lucide-react';
import { tareasDespachoAPI } from '../api';
import { toast } from '../hooks/use-toast';

const ESTADOS_PROPUESTA = {
  '': { label: 'Sin especificar', color: 'gray', icon: Clock },
  'pendiente': { label: 'Pendiente', color: 'yellow', icon: Clock },
  'realizada': { label: 'Realizada', color: 'green', icon: CheckCircle },
  'no_necesaria': { label: 'No necesaria', color: 'blue', icon: XCircle },
};

const getEstadoConfig = (estado) => {
  return ESTADOS_PROPUESTA[estado] || ESTADOS_PROPUESTA[''];
};

const Intereses = () => {
  const [minutas, setMinutas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroAnno, setFiltroAnno] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const [totales, setTotales] = useState({ realizada: 0, no_necesaria: 0, pendiente: 0, total: 0 });

  const cargarMinutas = async () => {
    setLoading(true);
    try {
      const response = await tareasDespachoAPI.minutas.getAll({ 
        fase_c_completada: true,
        limit: 500 
      });
      let minutasData = response.data.tareas || response.data || [];
      
      // Filtrar por estado de propuesta
      if (filtroEstado === 'sin_especificar') {
        minutasData = minutasData.filter(m => !m.estado_propuesta_intereses || m.estado_propuesta_intereses === '');
      } else if (filtroEstado !== 'todos') {
        minutasData = minutasData.filter(m => m.estado_propuesta_intereses === filtroEstado);
      }
      
      // Filtrar por anno
      if (filtroAnno && filtroAnno !== 'todos') {
        minutasData = minutasData.filter(m => {
          if (m.numero_minuta) {
            return m.numero_minuta.includes(`/${filtroAnno}`);
          }
          return false;
        });
      }
      
      // Filtrar por busqueda
      if (busqueda) {
        const searchLower = busqueda.toLowerCase();
        minutasData = minutasData.filter(m => 
          (m.numero_minuta && m.numero_minuta.toLowerCase().includes(searchLower)) ||
          (m.expediente_numero && m.expediente_numero.toLowerCase().includes(searchLower)) ||
          (m.descripcion_tarea && m.descripcion_tarea.toLowerCase().includes(searchLower))
        );
      }
      
      setMinutas(minutasData);
      
      // Calcular totales
      const allMinutas = response.data.tareas || response.data || [];
      const completadas = allMinutas.filter(m => m.fase_c_completada);
      setTotales({
        realizada: completadas.filter(m => m.estado_propuesta_intereses === 'realizada').length,
        no_necesaria: completadas.filter(m => m.estado_propuesta_intereses === 'no_necesaria').length,
        pendiente: completadas.filter(m => m.estado_propuesta_intereses === 'pendiente').length,
        sin_especificar: completadas.filter(m => !m.estado_propuesta_intereses || m.estado_propuesta_intereses === '').length,
        total: completadas.length
      });
    } catch (error) {
      console.error('Error cargando minutas:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar las minutas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarMinutas();
  }, [filtroEstado, filtroAnno]);

  const handleBuscar = () => {
    cargarMinutas();
  };

  const abrirMinuta = (minuta) => {
    window.open(`/tareas?tab=minutas&id=${minuta.id}`, '_blank');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-600" />
            Propuesta de Intereses
          </h1>
          <p className="text-gray-500">Control de propuestas de intereses en minutas completadas</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              if (exportandoExcel) return;
              setExportandoExcel(true);
              try {
                const token = localStorage.getItem('lexduo_token');
                const params = new URLSearchParams();
                if (filtroEstado && filtroEstado !== 'todos') params.append('estado_propuesta', filtroEstado === 'sin_especificar' ? 'sin_estado' : filtroEstado);
                if (busqueda) params.append('search', busqueda);
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/tareas-despacho/minutas/intereses/export-excel?${params.toString()}`;
                const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                if (!r.ok) throw new Error('Error en descarga');
                const blob = await r.blob();
                const cd = r.headers.get('Content-Disposition') || '';
                const m = cd.match(/filename="?([^";]+)"?/);
                const filename = m ? m[1] : `propuestas_intereses_${new Date().toISOString().slice(0,10)}.xlsx`;
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);
                setExportandoExcel(false);
              } catch (e) {
                setExportandoExcel(false);
                console.error('Error exportando Excel', e);
                alert('Error descargando Excel: ' + e.message);
              }
            }}
            data-testid="export-excel-intereses-btn"
            disabled={exportandoExcel}
          >
            {exportandoExcel ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generando…</>) : (<><Download className="w-4 h-4 mr-2" /> Excel</>)}
          </Button>
          <Button onClick={cargarMinutas} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Tarjetas de resumen */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFiltroEstado('todos')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-2xl font-bold">{totales.total}</p>
              </div>
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-green-500" onClick={() => setFiltroEstado('realizada')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Realizada</p>
                <p className="text-2xl font-bold text-green-600">{totales.realizada}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-yellow-500" onClick={() => setFiltroEstado('pendiente')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pendiente</p>
                <p className="text-2xl font-bold text-yellow-600">{totales.pendiente}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-blue-500" onClick={() => setFiltroEstado('no_necesaria')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">No necesaria</p>
                <p className="text-2xl font-bold text-blue-600">{totales.no_necesaria}</p>
              </div>
              <XCircle className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-gray-400" onClick={() => setFiltroEstado('sin_especificar')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Sin especificar</p>
                <p className="text-2xl font-bold text-gray-600">{totales.sin_especificar}</p>
              </div>
              <Clock className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>Buscar</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Numero minuta, expediente..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleBuscar()}
                />
                <Button onClick={handleBuscar}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="w-[150px]">
              <Label>Anno</Label>
              <Select value={filtroAnno} onValueChange={setFiltroAnno}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[180px]">
              <Label>Estado propuesta</Label>
              <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="realizada">Realizada</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="no_necesaria">No necesaria</SelectItem>
                  <SelectItem value="sin_especificar">Sin especificar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de minutas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Minutas ({minutas.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Cargando...</div>
          ) : minutas.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No hay minutas con los filtros seleccionados</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Numero</th>
                    <th className="text-left p-2">Expediente</th>
                    <th className="text-left p-2">Descripcion</th>
                    <th className="text-right p-2">Importe</th>
                    <th className="text-center p-2">Propuesta Intereses</th>
                    <th className="text-center p-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {minutas.map((minuta) => {
                    const estadoConfig = getEstadoConfig(minuta.estado_propuesta_intereses);
                    const IconEstado = estadoConfig.icon;
                    return (
                      <tr key={minuta.id} className="border-b hover:bg-gray-50">
                        <td className="p-2 font-medium">{minuta.numero_minuta || '-'}</td>
                        <td className="p-2">{minuta.expediente_numero || '-'}</td>
                        <td className="p-2 max-w-[300px] truncate">{minuta.descripcion_tarea || '-'}</td>
                        <td className="p-2 text-right">
                          {minuta.importe_minuta_final 
                            ? `${minuta.importe_minuta_final.toLocaleString('es-ES', { minimumFractionDigits: 2 })} EUR`
                            : '-'}
                        </td>
                        <td className="p-2 text-center">
                          <Badge className={`bg-${estadoConfig.color}-100 text-${estadoConfig.color}-700`}>
                            <IconEstado className="w-3 h-3 mr-1" />
                            {estadoConfig.label}
                          </Badge>
                        </td>
                        <td className="p-2 text-center">
                          <Button size="sm" variant="ghost" onClick={() => abrirMinuta(minuta)}>
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Intereses;
